import { useState, useEffect } from 'react';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import WhyUs from './components/WhyUs';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import CTA from './components/CTA';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Data Health Insurance Consultants",
  "url": SITE_URL,
  "description": "Houston's trusted independent health insurance broker. Customized PPO plans, Medicare supplements & group coverage. Open 24/7.",
  "publisher": {
    "@type": "Organization",
    "name": "Data Health Insurance Consultants",
    "url": SITE_URL
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "InsuranceAgency",
  "name": "Data Health Insurance Consultants",
  "description": "Independent broker specializing in private medical insurance for individuals and companies in Houston, TX. Expert advice and market comparisons.",
  "url": SITE_URL,
  "telephone": "+1-713-804-9039",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "1500 North Blvd Ste 5000 1006",
    "addressLocality": "Houston",
    "addressRegion": "TX",
    "postalCode": "77006",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 29.7392,
    "longitude": -95.3891
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
    "opens": "00:00",
    "closes": "23:59"
  },
  "priceRange": "$$",
  "areaServed": {
    "@type": "City",
    "name": "Houston",
    "sameAs": "https://en.wikipedia.org/wiki/Houston"
  },
  "sameAs": [
    "https://www.facebook.com/DataHealthInsuranceConsultants",
    "https://www.instagram.com/datahealthinsuranceconsultants/",
    "https://x.com/DataHealthInC",
    "https://www.linkedin.com/company/datahealthinsuranceconsultants/"
  ]
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What types of private health insurance plans do you offer in Houston?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We offer individual health plans, family coverage, company group plans, Medicare supplements, short-term health insurance, and specialized coverage options. As an independent broker, we compare plans from multiple carriers to find the best private health insurance for your needs in Houston."
      }
    },
    {
      "@type": "Question",
      "name": "How does your 24/7 availability work for health insurance consultations?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Data Health Insurance Consultants is open 24 hours a day, 7 days a week. You can call us at (713) 804-9039 anytime, submit a quote request online, or visit our Houston office at 1500 North Blvd Ste 5000 1006. We understand health insurance questions don\'t follow business hours."
      }
    },
    {
      "@type": "Question",
      "name": "Do you charge consultation fees for health insurance advice?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No, our consultations are completely free. As an independent insurance broker in Houston, we are compensated by insurance carriers, not by our clients. You receive expert advice and market comparisons at no cost to you."
      }
    },
    {
      "@type": "Question",
      "name": "What areas of Houston do you serve for private health insurance?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We serve all of Houston and the greater Houston metropolitan area, including The Heights, Montrose, Midtown, River Oaks, Memorial, Katy, Sugar Land, The Woodlands, Pearland, and surrounding communities in Harris County and beyond."
      }
    },
    {
      "@type": "Question",
      "name": "How quickly can I get private health insurance coverage in Houston?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Depending on the plan type, coverage can begin as soon as the next day for some short-term plans, or on the first of the following month for most major medical plans. Our Houston team works quickly to compare options and get you covered as fast as possible."
      }
    },
    {
      "@type": "Question",
      "name": "What is the difference between individual and group health insurance plans?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Individual health insurance covers a single person or family and is purchased directly. Group health insurance is offered by employers to their employees, often at lower rates due to risk pooling. Data Health Insurance Consultants in Houston helps with both types, comparing private health insurance options across multiple carriers."
      }
    }
  ]
};

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };
    injectSchema('schema-website', websiteSchema);
    injectSchema('schema-local-business', localBusinessSchema);
    injectSchema('schema-faq', faqSchema);
    return () => {
      ['schema-website', 'schema-local-business', 'schema-faq'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <main className="bg-white text-navy-900">
      <Navigation scrolled={scrolled} />
      <Hero />
      <Services />
      <About />
      <WhyUs />
      <Testimonials />
      <FAQ />
      <Contact />
      <CTA />
      <Footer />
    </main>
  );
}
